// ===== SHEET DETAIL MODAL =====
// Modal de detalhes da partitura com opcoes de download
// Suporta URL compartilhavel: /acervo/:categoria/:id
// Refatorado: extraido componentes e hook de download

import { useState, useEffect, useCallback, useRef, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Headphones } from 'lucide-react';
import { useAuth } from '@contexts/AuthContext';
import { useUI } from '@contexts/UIContext';
import { useData } from '@contexts/DataContext';
import { Icons } from '@constants/icons';
import { API } from '@services/api';
import CategoryIcon from '@components/common/CategoryIcon';
import { useMediaQuery } from '@hooks/useMediaQuery';
import { useSheetDownload, findParteExata } from '@hooks/useSheetDownload';
import { shareSheetLink } from '@utils/sheetShare';
import { PartePicker, DownloadConfirm, InstrumentSelector, ShareOptions } from './sheet';
import SheetAudioPlayer from './SheetAudioPlayer';

const PDFViewerModal = lazy(() => import('./PDFViewerModal'));

const SheetDetailModal = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { selectedSheet, setSelectedSheet, showToast, addToShareCart, removeFromShareCart, shareCart } = useUI();
  const { favoritesSet, toggleFavorite, categoriesMap, instrumentNames } = useData();

  // Estado local
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [showInstrumentPicker, setShowInstrumentPicker] = useState(false);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const showShareOptionsRef = useRef(false);
  const [partes, setPartes] = useState([]);
  const [loadingPartes, setLoadingPartes] = useState(false);
  const [mediaDetails, setMediaDetails] = useState(null);

  // Hook de download
  const download = useSheetDownload({
    showToast,
    selectedSheet,
    partes
  });
  const selectedSheetId = selectedSheet?.id;

  // O repertório fornece dados resumidos e o cache da sessão pode estar antigo.
  // Revalidar ao abrir garante que o modal use a mídia cadastrada no admin.
  useEffect(() => {
    if (!selectedSheetId) return;
    let cancelled = false;
    setMediaDetails(null);

    API.getPartitura(selectedSheetId).then(data => {
      if (!cancelled && data) setMediaDetails({ sheetId: selectedSheetId, data });
    }).catch(error => {
      if (!cancelled) console.error('Erro ao buscar mídia da partitura:', error);
    });

    return () => { cancelled = true; };
  }, [selectedSheetId]);

  const currentMedia = mediaDetails && mediaDetails.sheetId === selectedSheetId ? mediaDetails.data : null;
  const audioSheet = selectedSheet ? {
    ...selectedSheet,
    hasAudio: currentMedia?.has_audio ?? selectedSheet.hasAudio,
    audioName: currentMedia ? currentMedia.audio_name : selectedSheet.audioName,
    audioMime: currentMedia ? currentMedia.audio_mime : selectedSheet.audioMime,
    youtubeUrl: currentMedia && 'youtube_url' in currentMedia ? currentMedia.youtube_url : selectedSheet.youtubeUrl
  } : null;

  useEffect(() => {
    showShareOptionsRef.current = showShareOptions;
  }, [showShareOptions]);

  // Handler para adicionar parte ao carrinho de compartilhamento
  const handleAddToCart = useCallback((instrument) => {
    if (!selectedSheet) return;

    const parte = findParteExata(instrument, partes);
    if (parte) {
      addToShareCart({
        sheetId: selectedSheet.id,
        parteId: parte.id,
        sheetTitle: selectedSheet.title,
        instrument: parte.instrumento
      });
      showToast(`${parte.instrumento} adicionado ao carrinho`);
    } else {
      showToast('Parte não encontrada', 'error');
    }
  }, [selectedSheet, partes, addToShareCart, showToast]);

  // Refs para focus trap
  const modalRef = useRef(null);
  const previouslyFocusedElement = useRef(null);

  // Handler para fechar modal
  const handleClose = useCallback(() => {
    const fallbackPath = selectedSheet?.category ? `/acervo/${selectedSheet.category}` : '/acervo';
    setSelectedSheet(null);
    // Se estamos numa URL de partitura, volta para a tela de origem
    if (location.pathname.includes('/acervo/') && location.pathname.split('/').length > 3) {
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(fallbackPath, { replace: true });
      }
    }
  }, [location.pathname, navigate, setSelectedSheet, selectedSheet]);

  // Travar scroll do body quando modal estiver aberto + focus trap + Escape key
  useEffect(() => {
    if (selectedSheet) {
      // Salvar posição atual do scroll
      const scrollY = window.scrollY;

      // Aplicar classe que trava scroll (definida em base.css com !important)
      document.documentElement.classList.add('modal-open');
      document.body.style.top = `-${scrollY}px`;

      // Salvar elemento com foco atual
      previouslyFocusedElement.current = document.activeElement;

      // Mover foco para o modal quando abrir
      if (modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusableElements.length > 0) {
          focusableElements[0].focus();
        }
      }

      // Handler para Escape
      const handleKeyDown = (e) => {
        if (e.key === 'Escape' && !showShareOptionsRef.current) {
          handleClose();
        }
      };

      document.addEventListener('keydown', handleKeyDown);

      return () => {
        document.removeEventListener('keydown', handleKeyDown);
        // Restaurar scroll
        document.documentElement.classList.remove('modal-open');
        document.body.style.top = '';
        window.scrollTo(0, scrollY);
        // Restaurar foco anterior
        if (previouslyFocusedElement.current) {
          previouslyFocusedElement.current.focus();
        }
      };
    }
  }, [selectedSheet, handleClose]);

  // Buscar partes quando abrir o modal
  useEffect(() => {
    if (!selectedSheet) return;

    let cancelled = false;
    setShowInstrumentPicker(false);
    setShowShareOptions(false);
    download.handleCancelDownload();

    API.trackEvent({
      tipo: 'partitura_aberta',
      origem: 'detalhe_partitura',
      partitura_id: selectedSheetId,
    });

    const fetchPartes = async () => {
      setLoadingPartes(true);
      try {
        const data = await API.getPartesPartitura(selectedSheetId);
        if (!cancelled) {
          setPartes(data || []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('Erro ao buscar partes:', e);
          setPartes([]);
        }
      }
      if (!cancelled) {
        setLoadingPartes(false);
      }
    };

    fetchPartes();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- download é estável (useSheetDownload)
  }, [selectedSheetId]);

  // Dados derivados do selectedSheet (só acessados quando existe)
  const category = selectedSheet ? categoriesMap.get(selectedSheet.category) : null;
  const isFavorite = selectedSheet ? favoritesSet.has(selectedSheet.id) : false;
  const userInstrument = user?.instrument || 'Trompete Bb';
  const userInstrumentLower = userInstrument?.toLowerCase() || '';
  const isMaestro = userInstrumentLower === 'maestro' || userInstrumentLower === 'regente';
  const hasGrade = partes.some(p => p.instrumento?.toLowerCase() === 'grade');
  const availableInstruments = partes.length > 0
    ? [...new Set(partes.map(p => p.instrumento))]
    : instrumentNames;

  const handleShareLink = useCallback(async () => {
    setShowShareOptions(false);
    try {
      const result = await shareSheetLink({
        sheet: selectedSheet,
        categoryName: category?.name
      });
      if (result.status === 'shared') {
        showToast(result.copied ? 'Link compartilhado e copiado!' : 'Link compartilhado!');
      } else if (result.status === 'copied') {
        showToast('Link copiado!');
      }
      if (result.status !== 'cancelled') {
        API.trackEvent({
          tipo: 'partitura_compartilhada_link',
          origem: 'detalhe_partitura',
          partitura_id: selectedSheet.id
        });
      }
    } catch (error) {
      console.error('Erro ao compartilhar link:', error);
      showToast('Não foi possível compartilhar o link', 'error');
    }
  }, [category?.name, selectedSheet, showToast]);

  const handleSendCopy = useCallback(() => {
    setShowShareOptions(false);
    download.handleShareInstrument(isMaestro ? 'Grade' : userInstrument);
  }, [download, isMaestro, userInstrument]);

  const downloadSummary = (
    <span className="sheet-audio-downloads" aria-label={`${selectedSheet?.downloads || 0} downloads`}>
      <Download size={15} aria-hidden="true" />
      <span>{selectedSheet?.downloads || 0}</span>
    </span>
  );

  return (
    <AnimatePresence>
      {selectedSheet && (
        <>
          {/* Overlay */}
          <motion.div
            key="sheet-modal-overlay"
            onClick={handleClose}
            role="presentation"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              backdropFilter: 'blur(4px)',
              WebkitBackdropFilter: 'blur(4px)',
              zIndex: 2000
            }}
          />

          {/* Modal de Selecao de Partes */}
          <PartePicker
            isOpen={download.showPartePicker}
            partes={download.partesDisponiveis}
            instrumentName={download.confirmInstrument}
            downloading={download.downloading}
            onSelectParte={download.downloadParteDireta}
            onClose={download.closePartePicker}
          />

          {/* Modal de Confirmacao */}
          <DownloadConfirm
            isOpen={!!download.confirmInstrument && !download.showPartePicker}
            instrumentName={download.confirmInstrument}
            downloading={download.downloading}
            onConfirm={download.handleConfirmDownload}
            onCancel={download.handleCancelDownload}
          />

          <ShareOptions
            isOpen={showShareOptions}
            onClose={() => setShowShareOptions(false)}
            onSendCopy={handleSendCopy}
            onShareLink={handleShareLink}
            copyDisabled={!download.canShareFiles() || download.downloading || loadingPartes || (isMaestro && !hasGrade)}
          />

          {/* Visualizador de PDF embutido */}
          {download.pdfViewer.isOpen && (
            <Suspense fallback={<div className="flex items-center justify-center p-8">Carregando visualizador...</div>}>
              <PDFViewerModal
                isOpen={download.pdfViewer.isOpen}
                pdfUrl={download.pdfViewer.url}
                title={`${download.pdfViewer.title} - ${download.pdfViewer.instrument}`}
                onClose={download.closePdfViewer}
                onDownload={() => {
                  // Abre em nova aba como fallback
                  if (download.pdfViewer.url) {
                    window.open(download.pdfViewer.url, '_blank', 'noopener,noreferrer');
                  }
                }}
              />
            </Suspense>
          )}

          {/* Modal Principal */}
          <motion.div
            ref={modalRef}
            key="sheet-modal-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sheet-detail-title"
            initial={isDesktop ? { opacity: 0, scale: 0.95 } : { opacity: 1, y: '100%' }}
            animate={isDesktop ? { opacity: 1, scale: 1 } : { opacity: 1, y: 0 }}
            exit={isDesktop ? { opacity: 0, scale: 0.95 } : { opacity: 1, y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            style={{
              position: 'fixed',
              ...(isDesktop ? {
                top: '50%',
                left: '50%',
                x: '-50%',
                y: '-50%',
                width: '420px',
                maxWidth: '90vw',
                borderRadius: '20px'
              } : {
                bottom: 0,
                left: 0,
                right: 0,
                borderRadius: '24px 24px 0 0'
              }),
              background: 'var(--bg-card)',
              zIndex: 2001,
              maxHeight: isDesktop ? '85vh' : '90vh',
              overflow: 'hidden',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Handle - apenas mobile */}
            {!isDesktop && (
              <div style={{
                width: '40px',
                height: '4px',
                background: 'var(--border)',
                borderRadius: '2px',
                margin: '12px auto',
                flexShrink: 0
              }} />
            )}

            {/* Header com imagem */}
            <div style={{
              background: 'linear-gradient(145deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)',
              padding: isDesktop ? '20px' : '16px 20px',
              position: 'relative',
              overflow: 'hidden',
              flexShrink: 0
            }}>
              <div style={{
                position: 'absolute',
                top: '-20px',
                right: '-20px',
                width: '100px',
                height: '100px',
                background: 'rgba(212, 175, 55, 0.1)',
                borderRadius: '50%'
              }} />

              <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
                <div
                  style={{
                    width: '56px',
                    height: '56px',
                    borderRadius: '14px',
                    background: 'linear-gradient(145deg, #3a3a4a 0%, #2a2a38 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <CategoryIcon categoryId={category?.id} size={26} color="var(--accent)" />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <h2
                    id="sheet-detail-title"
                    style={{
                      fontSize: '17px',
                      fontWeight: '700',
                      color: 'var(--text-primary)',
                      marginBottom: '3px',
                      lineHeight: '1.3'
                    }}
                  >
                    {selectedSheet.title}
                  </h2>
                  <p style={{
                    fontSize: '13px',
                    color: 'var(--text-muted)',
                    marginBottom: selectedSheet.arranger ? '2px' : '6px'
                  }}>{selectedSheet.composer}</p>
                  {selectedSheet.arranger && (
                    <p style={{
                      fontSize: '12px',
                      color: 'var(--text-muted)',
                      marginBottom: '6px'
                    }}>
                      Arr: {selectedSheet.arranger}
                    </p>
                  )}
                  <span style={{
                    display: 'inline-block',
                    padding: '3px 8px',
                    background: 'rgba(212, 175, 55, 0.15)',
                    color: 'var(--accent)',
                    borderRadius: '6px',
                    fontSize: '10px',
                    fontWeight: '600',
                    }}>{category?.name}</span>
                </div>

                <button
                  onClick={handleClose}
                  aria-label="Fechar modal"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'var(--bg-secondary)',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    flexShrink: 0
                  }}
                >
                  <div style={{ width: '16px', height: '16px' }}><Icons.Close /></div>
                </button>
              </div>
            </div>

            {/* Conteudo scrollavel */}
            <div style={{
              padding: isDesktop ? '16px 20px 20px' : '16px 20px 28px',
              overflowY: 'auto',
              flex: 1
            }}>
              {/* Info compacta */}
              {(selectedSheet.year || selectedSheet.featured) && <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                {selectedSheet.year && (
                  <div style={{ background: 'var(--bg-secondary)', padding: '8px 12px', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', }}>
                      {selectedSheet.year}
                    </span>
                  </div>
                )}
                {selectedSheet.featured && (
                  <div style={{ background: 'rgba(212, 175, 55, 0.15)', padding: '8px 12px', borderRadius: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="var(--accent)" stroke="none" aria-hidden="true">
                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                      </svg>
                      Destaque
                    </span>
                  </div>
                )}
              </div>}

                <div>
                  {audioSheet.hasAudio ? <SheetAudioPlayer key={selectedSheet.id} sheet={audioSheet} summary={downloadSummary} /> : (
                    <section className="sheet-audio" aria-label="Áudio da partitura">
                      <div className="sheet-audio-summary">
                        {downloadSummary}
                        <div className="sheet-audio-unavailable">
                          <Headphones size={17} aria-hidden="true" />
                          <span>Áudio ainda não disponível</span>
                        </div>
                      </div>
                    </section>
                  )}
                  {audioSheet.youtubeUrl && (
                    <a href={audioSheet.youtubeUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', color: 'var(--accent)', fontSize: 12, fontWeight: 700, marginBottom: 14, textDecoration: 'underline' }}>
                      Ver no YouTube ↗
                    </a>
                  )}
                </div>

              {/* Opções de Download / Ações Rápidas */}
              <div style={{ marginBottom: '14px' }}>
                <p style={{
                  fontSize: '10px',
                  color: 'var(--text-muted)',
                  marginBottom: '8px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  fontWeight: '600'
                }}>Ações para {isMaestro ? 'Grade' : userInstrument}</p>

                {/* Botões de Ação Rápida (Visualizar / Imprimir) */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <button
                    onClick={() => download.handleViewInstrument(isMaestro ? 'Grade' : userInstrument)}
                    disabled={download.downloading || loadingPartes || (isMaestro && !hasGrade)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '10px',
                      background: 'rgba(52, 152, 219, 0.1)',
                      border: '1.5px solid rgba(52, 152, 219, 0.3)',
                      color: '#3498db',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: (isMaestro && !hasGrade) || download.downloading || loadingPartes ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <div style={{ width: '14px', height: '14px' }}><Icons.Eye /></div>
                    Visualizar
                  </button>

                  <button
                    onClick={() => download.handlePrintInstrument(isMaestro ? 'Grade' : userInstrument)}
                    disabled={download.downloading || loadingPartes || (isMaestro && !hasGrade)}
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '10px',
                      background: 'rgba(46, 204, 113, 0.1)',
                      border: '1.5px solid rgba(46, 204, 113, 0.3)',
                      color: '#2ecc71',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: (isMaestro && !hasGrade) || download.downloading || loadingPartes ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <div style={{ width: '14px', height: '14px' }}><Icons.Printer /></div>
                    Imprimir
                  </button>
                </div>

                {/* Botão Baixar - Meu Instrumento */}
                <button
                  data-walkthrough="quick-download"
                  onClick={() => download.handleSelectInstrument(isMaestro ? 'Grade' : userInstrument)}
                  aria-label={isMaestro ? 'Baixar grade' : `Baixar partitura para ${userInstrument}`}
                  disabled={loadingPartes || download.downloading || (isMaestro && !hasGrade)}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    borderRadius: '10px',
                    background: (loadingPartes || download.downloading || (isMaestro && !hasGrade)) ? 'var(--bg-secondary)' : 'linear-gradient(145deg, #722F37 0%, #5C1A1B 100%)',
                    border: 'none',
                    color: (loadingPartes || download.downloading || (isMaestro && !hasGrade)) ? 'var(--text-muted)' : '#F4E4BC',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: (isMaestro && !hasGrade) ? 'not-allowed' : ((loadingPartes || download.downloading) ? 'wait' : 'pointer'),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '12px',
                    boxShadow: (loadingPartes || download.downloading || (isMaestro && !hasGrade)) ? 'none' : '0 4px 12px rgba(114, 47, 55, 0.3)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ width: '16px', height: '16px' }}><Icons.Download /></div>
                    <span>{loadingPartes ? 'Carregando...' : (isMaestro ? 'Baixar Grade' : 'Meu Instrumento')}</span>
                  </div>
                  <span style={{
                    background: 'rgba(244, 228, 188, 0.2)',
                    padding: '3px 8px',
                    borderRadius: '5px',
                    fontSize: '10px',
                    fontWeight: '700'
                  }}>{isMaestro ? 'Grade' : userInstrument}</span>
                </button>

                {/* Seletor de Outros Instrumentos */}
                <div data-walkthrough="instrument-selector">
                  <InstrumentSelector
                    isOpen={showInstrumentPicker}
                    instruments={availableInstruments}
                    userInstrument={userInstrument}
                    isMaestro={isMaestro}
                    downloading={download.downloading}
                    canShare={download.canShareFiles()}
                    shareCart={shareCart.filter(item => item.sheetId === selectedSheet.id)}
                    onToggle={() => setShowInstrumentPicker(!showInstrumentPicker)}
                    onSelectInstrument={download.handleSelectParteEspecifica}
                    onPrintInstrument={download.handlePrintInstrument}
                    onViewInstrument={download.handleViewInstrument}
                    onShareInstrument={download.handleShareInstrument}
                    onAddToCart={handleAddToCart}
                    onRemoveFromCart={removeFromShareCart}
                  />
                </div>
              </div>

              {/* Botoes Adicionais */}
              <div data-walkthrough="sheet-options" style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => setShowShareOptions(true)}
                    aria-label="Compartilhar partitura"
                    style={{
                      flex: 1,
                      padding: '10px',
                      borderRadius: '10px',
                      background: 'rgba(37, 211, 102, 0.1)',
                      border: '1.5px solid rgba(37, 211, 102, 0.3)',
                      color: '#25D366',
                      fontSize: '12px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '6px'
                    }}
                  >
                    <div style={{ width: '14px', height: '14px' }}><Icons.Share /></div>
                    Enviar
                  </button>

                <button
                  onClick={() => toggleFavorite(selectedSheet.id)}
                  aria-pressed={isFavorite}
                  aria-label={isFavorite ? `Remover dos favoritos` : `Adicionar aos favoritos`}
                  style={{
                    flex: 1,
                    padding: '10px',
                    borderRadius: '10px',
                    background: isFavorite ? 'rgba(232,90,79,0.1)' : 'transparent',
                    border: isFavorite ? '1.5px solid var(--primary)' : '1.5px solid var(--border)',
                    color: isFavorite ? 'var(--primary)' : 'var(--text-muted)',
                    fontSize: '12px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px'
                  }}
                >
                  <div style={{ width: '14px', height: '14px' }}><Icons.Heart filled={isFavorite} /></div>
                  {isFavorite ? 'Favorito' : 'Favoritar'}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SheetDetailModal;
